import React, {
  useCallback,
  useEffect, useMemo, useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { Api } from '@shell/shell-ui';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import includes from 'lodash/includes';
import trimStart from 'lodash/trimStart';

import Loader from '@components/loader/Loader';

import NavigationLink from '@components/navbar/NavigationLink';
import useNotifications from './useNotifications';
import NotificationItem from './NotificationItem';

export default function Navigation({
  // eslint-disable-next-line react/prop-types
  handleSearchActive,
}) {
  const location = useLocation();
  const [mobileMenuActive, setMenuActive] = useState(false);
  const [logoutUrl, setLogoutUrl] = useState('');
  const currentUser = useSelector((state) => state.user.loggedUser);
  const notificationsListRef = useRef(null);
  const [mobileSubmenuVisible, setMobileSubmenuVisible] = useState(false);
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [confirmUsers, setConfirmUsers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [appsUrls, setAppsUrls] = useState({});
  const {
    loading: loadingNotifications,
    notificationsList,
    loadMoreNotifications,
  } = useNotifications();

  const isContactPerson = useMemo(() => {
    if (!currentUser) {
      return false;
    }
    return currentUser.isContactPerson;
  }, [currentUser]);

  const isAdmin = useMemo(() => {
    if (!currentUser) {
      return false;
    }
    return currentUser.userRoles.includes('ROLE_ADMIN') || currentUser.userRoles.includes('ROLE_SUPER_ADMIN');
  }, [currentUser]);

  const unreadedNotifications = useMemo(() => (
    notificationsList
      .filter(({ status }) => status === 'pending')
      .length
  ), [notificationsList]);

  const toggleMobileMenu = useCallback((state) => {
    const newState = state ?? !mobileMenuActive;

    if (newState) {
      document.body.classList.add('menu-active');
    } else {
      setMobileSubmenuVisible(false);
      document.body.classList.remove('menu-active');
    }

    setMenuActive(newState);
  }, [mobileMenuActive]);

  const resizeHandler = useCallback(() => {
    if (window.innerWidth >= 960 && mobileMenuActive) {
      toggleMobileMenu(false);
    }

    if (window.innerWidth < 960 && notificationsVisible) {
      setNotificationsVisible(false);
    }
  }, [mobileMenuActive, notificationsVisible, toggleMobileMenu]);

  const handlerHideNotifications = useCallback(({ target }) => {
    const notificationsElement = document.querySelector('.navigation__profile__notifications');

    if (!notificationsElement.contains(target)) {
      setNotificationsVisible(false);
    }
  }, []);

  const updateNotificationStatus = useCallback(async (notifyID) => {
    await Api.post(`/notification-user/${notifyID}/status`);
    await loadMoreNotifications(0);
  }, []);

  useEffect(() => {
    const getLogOutUrl = async () => {
      try {
        const { data } = await Api.get('/logout/service');
        setLogoutUrl(data);
      } catch (error) {
        toast.error('Unable to retrieve logout url');
      }
    };
    const getHeaderLinks = async () => {
      try {
        const { data } = await Api.get('/header');
        const links = JSON.parse(data.settings);
        setAppsUrls(data.links);
        setMenuItems(links);
      } catch (e) {
        console.log({ e });
      }
    };

    getLogOutUrl();
    getHeaderLinks();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler, { passive: true });
    document.body.addEventListener('click', handlerHideNotifications);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      document.body.removeEventListener('click', handlerHideNotifications);
    };
  }, [handlerHideNotifications, mobileMenuActive, notificationsVisible, resizeHandler]);

  useEffect(() => {
    setMenuActive(false);
    setMobileSubmenuVisible(false);
    setNotificationsVisible(false);
    document.body.classList.remove('menu-active');
  }, [location]);

  const isMenuItemActive = useCallback((activeItems) => {
    const path = trimStart(location.pathname, '/');
    return includes(activeItems, path);
  }, [location]);

  useEffect(() => {
    async function loadData() {
      if (isAdmin || isContactPerson) {
        const { data } = await Api.get('/register/user/not-confirmed');
        setConfirmUsers(data.content);
      }
    }
    loadData();
  }, [isAdmin, isContactPerson]);

  const notificationsNumber = unreadedNotifications + (confirmUsers.length ? 1 : 0 ?? 0);

  return (
    <div className='navigation__menu'>
      <ul className='navigation__menu-list'>
        {menuItems.map((link) => (
          <NavigationLink
            key={link.link}
            {...link}
            urls={appsUrls}
            isMenuItemActive={isMenuItemActive}
          />
        ))}
      </ul>
      <button
        aria-label='navigation-search'
        type='button'
        className='navigation__search'
        onClick={handleSearchActive}
      >
        <i className='icon--search' />
      </button>
      <button
        aria-label='navigation-hamburger'
        type='button'
        className='navigation__hamburger'
        onClick={() => toggleMobileMenu(!mobileMenuActive)}
      >
        {mobileMenuActive ? <i className='icon--thickclose' /> : <i className='icon--burgerbun' />}
      </button>
      <div className='navigation__profile'>
        <div className='navigation__profile__notifications'>
          <button
            aria-label='navigation-notification'
            type='button'
            className='navigation__profile__btn'
            onClick={() => {
              setNotificationsVisible((old) => !old);
            }}
          >
            <i className='icon--alarm'>
              <span aria-label={`notifications ${notificationsNumber}`}>
                {notificationsNumber}
              </span>
            </i>
          </button>
          <div
            ref={notificationsListRef}
            className={`navigation__profile__list ${notificationsVisible ? 'active' : ''}`}
          >
            {loadingNotifications ? (
              <Loader loaderSize={50} />
            ) : (
              <ul>
                {(confirmUsers.length > 0) && (
                  <li>
                    <Link
                      to='/contact-person'
                    />
                    {confirmUsers.length > 0 && (
                      <p className='title'>
                        You have
                        {' '}
                        {confirmUsers.length}
                        {' '}
                        new user confirmation requests.
                      </p>
                    )}
                  </li>
                )}
                {notificationsList.map(({ id, notification, status }) => (
                  <NotificationItem
                    key={id}
                    id={id}
                    status={status}
                    notification={notification}
                    updateNotificationStatus={updateNotificationStatus}
                  />
                ))}
                <li className='last'>
                  <Link
                    to='/profile#notification'
                    className='btn btn--block btn--size-md'
                  >
                    Show more
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className='navigation__profile__dropdown'>
          <li className={`navigation__profile__dropdown__link ${isMenuItemActive(['profile']) ? 'navigation__profile__dropdown__link--active' : ''}`}>
            <NavLink
              to='/profile'
              className={({ isActive }) => `navigation__profile__dropdown__link--item${isActive ? ' navigation__profile__dropdown__link--item-selected' : ''}`}
            >
              {`${currentUser.name} ${currentUser.surname}`}
            </NavLink>
            <i className='icon--down' />
          </li>
          <ul>
            <li>
              <NavLink
                to='/profile'
                className={({ isActive }) => (isActive ? 'navigation__profile__dropdown__link--item-active' : '')}
              >
                My profile
              </NavLink>
            </li>
            {isAdmin && (
              <li>
                {/* eslint-disable-next-line no-undef */}
                <a href={`${ADMIN_URL}`}>
                  Admin panel
                </a>
              </li>
            )}
            {logoutUrl && (
              <li>
                <a href={logoutUrl}>
                  Logout
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* MOBILE */}
      <div className={`navigation__mobile ${mobileMenuActive ? 'active' : ''}`}>
        <ul className='navigation__mobile__menu'>
          {menuItems.map((link) => (
            <NavigationLink
              key={link.link}
              isMobile
              {...link}
              urls={appsUrls}
              isMenuItemActive={isMenuItemActive}
              mobileSubmenuVisible={mobileSubmenuVisible}
              setMobileSubmenuVisible={setMobileSubmenuVisible}
            />
          ))}
        </ul>
        <div className='navigation__mobile__user'>
          <NavLink
            to='/profile'
            className={({ isActive }) => (isActive ? 'navigation__mobile__user--item-active' : '')}
          >
            {`${currentUser.name} ${currentUser.surname}`}
          </NavLink>
          {isAdmin && (
            // eslint-disable-next-line no-undef
            <a href={`${ADMIN_URL}`}>
              Admin panel
            </a>
          )}
          {logoutUrl
            && (
              <a
                href={logoutUrl}
                className='navigation__mobile__user__item--logout'
              >
                Logout
                <i className='icon--globallocal' />
              </a>
            )}
        </div>
        <div className='navigation__mobile__notifications'>
          <i className='icon--alarm'>
            <span>
              {unreadedNotifications + (confirmUsers.length ? 1 : 0 ?? 0)}
            </span>
          </i>
          Notifications
        </div>
        {!loadingNotifications && (
          <>
            <ul className='navigation__notifications__list'>
              {(confirmUsers.length > 0) && (
                <li>
                  <Link
                    to='/contact-person'
                  />
                  {confirmUsers.length > 0 && (
                    <p className='title'>
                      You have
                      {' '}
                      {confirmUsers.length}
                      {' '}
                      new user confirmation requests.
                    </p>
                  )}
                </li>
              )}
              {notificationsList.map(({ id, notification, status }) => (
                <NotificationItem
                  key={id}
                  id={id}
                  status={status}
                  notification={notification}
                  updateNotificationStatus={updateNotificationStatus}
                />
              ))}
            </ul>
            <Link
              to='/profile#notification'
              className='navigation__notifications__list__button'
            >
              Show more
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
